import type { HdcReport } from "../../../../../ExperianPrivateResponse.types";
import {
  mapChecks,
  mapKpis,
  mapPerson,
  mapProductResult,
  mapScore,
} from "../mappers";
import { C, tabular, toneColor } from "../theme";
import { Card, InfoGrid } from "../shared/Card";
import { KpiTile } from "../shared/KpiTile";
import { ProductPill } from "../shared/ProductPill";

const grid = (min: number) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
  gap: 20,
});

export const ResumenTab = ({
  report,
  executionDatetime,
}: {
  report: HdcReport;
  executionDatetime?: string | null;
}) => {
  const product = mapProductResult(report, executionDatetime);
  const checks = mapChecks(report);
  const person = mapPerson(report);
  const score = mapScore(report);
  const kpis = mapKpis(report);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={grid(280)}>
        <Card title="Consulta" aside={<ProductPill product={product} />}>
          <InfoGrid
            items={[
              { label: "Fecha de consulta", value: product.consultDate },
              {
                label: "Código de seguridad",
                value: product.securityCode,
                valueStyle: tabular,
              },
              { label: "Ejecución del paso", value: product.executionDatetime },
            ]}
          />
          <div
            style={{
              borderTop: `1px solid ${C.line}`,
              paddingTop: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              fontSize: 13,
            }}
          >
            {checks.map((c) => (
              <div
                key={c.label}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <span style={{ color: C.white }}>{c.label}</span>
                <span style={{ fontWeight: 600, color: toneColor(c.tone) }}>
                  {c.value}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Titular">
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: C.white }}>
              {person.fullName}
            </span>
            <span style={{ fontSize: 13, color: C.muted }}>
              {person.idDesc} · {person.idNumber}
            </span>
          </div>
          <InfoGrid
            items={[
              {
                label: "Estado documento",
                value: person.status,
                valueStyle: { fontWeight: 600, color: C.ok },
              },
              { label: "Expedición", value: person.issue },
              { label: "Rango de edad", value: person.age },
              { label: "Género", value: person.gender },
            ]}
          />
        </Card>

        {score && (
          <Card
            title="Score"
            aside={`${score.model} · ${score.date}`}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span
                style={{
                  ...tabular,
                  fontSize: 44,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: C.white,
                }}
              >
                {score.value}
              </span>
              <span style={{ fontSize: 13, color: C.muted }}>
                de {score.max}
              </span>
            </div>
            <div
              role="meter"
              aria-label="Score"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={score.pct ?? 0}
              style={{
                position: "relative",
                height: 8,
                borderRadius: 4,
                background: C.track,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${score.pct ?? 0}%`,
                  borderRadius: 4,
                  background: C.ok,
                }}
              />
            </div>
            {score.reasons.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  fontSize: 12,
                  color: C.text,
                }}
              >
                <span style={{ fontWeight: 700, color: C.white }}>Razones</span>
                {score.reasons.map((r, i) => (
                  <span key={i} style={tabular}>
                    {r}
                  </span>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {kpis.length > 0 && (
        <>
          <section
            aria-label="Indicadores agregados"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: 12,
            }}
          >
            {kpis.map((k) => (
              <KpiTile key={k.label} kpi={k} />
            ))}
          </section>
          <p style={{ margin: "-12px 0 0", fontSize: 12, color: C.muted }}>
            Valores monetarios en miles de pesos (COP), tal como los reporta
            Experian.
          </p>
        </>
      )}
    </div>
  );
};
